import React, { useState } from 'react';
import { Search, Loader2, AlertTriangle, Info, ChevronDown, ChevronUp, FileText } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const PARAMETER_OPTIONS = {
  setting: {
    label: 'Setting',
    options: [
      { value: '', label: 'Any / not specified' },
      { value: 'domestic', label: 'Domestic / Residential' },
      { value: 'commercial', label: 'Commercial / Industrial' },
      { value: 'temporary', label: 'Temporary / Event' }
    ]
  },
  vesselType: {
    label: 'Vessel Type',
    options: [
      { value: '', label: 'Any / not specified' },
      { value: 'bulk', label: 'Bulk Storage Vessel' },
      { value: 'cylinder', label: 'Cylinder / Portable' }
    ]
  },
  installationType: {
    label: 'Installation',
    options: [
      { value: '', label: 'Any / not specified' },
      { value: 'above ground', label: 'Above Ground' },
      { value: 'underground', label: 'Underground / Buried' },
      { value: 'mounded', label: 'Mounded' }
    ]
  },
  location: {
    label: 'Location',
    options: [
      { value: '', label: 'Any / not specified' },
      { value: 'indoor', label: 'Indoor / Enclosed' },
      { value: 'outdoor', label: 'Outdoor / Open Air' }
    ]
  },
  gasType: {
    label: 'Gas Type',
    options: [
      { value: '', label: 'Any / not specified' },
      { value: 'propane', label: 'Propane' },
      { value: 'butane', label: 'Butane' },
      { value: 'LPG mix', label: 'LPG Mixture' }
    ]
  }
};

const RISK_COLOURS = {
  critical: { bg: 'bg-red-900/30', border: 'border-red-500/30', text: 'text-red-400', badge: 'bg-red-900/30 text-red-400' },
  elevated: { bg: 'bg-amber-900/20', border: 'border-amber-500/30', text: 'text-amber-400', badge: 'bg-amber-900/20 text-amber-400' },
  standard: { bg: 'bg-pvs-card-light', border: 'border-pvs-border', text: 'text-pvs-text-muted', badge: 'bg-pvs-card-light text-pvs-text-muted' }
};

const OBLIGATION_COLOURS = {
  MANDATORY: 'bg-red-900/30 text-red-400',
  RECOMMENDED: 'bg-amber-900/20 text-amber-400',
  PERMISSIVE: 'bg-pvs-accent/10 text-pvs-accent',
  INFORMATIONAL: 'bg-pvs-card-light text-pvs-text-muted'
};

const ScenarioAnalysis = () => {
  const [query, setQuery] = useState('');
  const [parameters, setParameters] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedClauses, setExpandedClauses] = useState(new Set());

  const handleAnalyse = async () => {
    if (!query.trim()) return;

    setLoading(true);
    setError(null);

    const filteredParams = Object.fromEntries(
      Object.entries(parameters).filter(([, v]) => v)
    );

    try {
      const response = await fetch(`${API_BASE}/api/vesselmind/scenario`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: query.trim(), parameters: filteredParams })
      });

      if (!response.ok) throw new Error('Analysis failed');

      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleClause = (factId) => {
    setExpandedClauses(prev => {
      const next = new Set(prev);
      if (next.has(factId)) next.delete(factId);
      else next.add(factId);
      return next;
    });
  };

  const ds = result?.decisionSupport;

  return (
    <div className="bg-pvs-card border border-pvs-accent/30 rounded-xl shadow-pvs-glow flex flex-col h-[calc(100vh-200px)]">
      {/* Header */}
      <div className="border-b border-pvs-border p-4">
        <div className="flex items-center gap-2 mb-1">
          <Search className="w-5 h-5 text-pvs-accent" />
          <h2 className="font-semibold text-white">Scenario Analysis</h2>
        </div>
        <p className="text-xs text-pvs-text-dim">
          Describe your scenario to identify all potentially applicable clauses across the Codes of Practice.
        </p>
      </div>

      {/* Input Area */}
      <div className="p-4 border-b border-pvs-border space-y-3">
        <textarea
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Describe your scenario (e.g. installing a bulk LPG vessel at a commercial premises with specific distance constraints...)"
          className="w-full px-3 py-2 bg-pvs-input-bg border border-pvs-input-border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-pvs-accent/50 focus:border-pvs-accent/50 placeholder-pvs-text-dim resize-none text-sm"
          rows="2"
          disabled={loading}
        />

        {/* Structured Parameters */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          {Object.entries(PARAMETER_OPTIONS).map(([key, config]) => (
            <div key={key}>
              <label className="text-xs font-medium text-pvs-text-muted block mb-1">{config.label}</label>
              <select
                value={parameters[key] || ''}
                onChange={(e) => setParameters(prev => ({ ...prev, [key]: e.target.value }))}
                className="w-full text-xs px-2 py-1.5 bg-pvs-input-bg border border-pvs-input-border text-white rounded focus:outline-none focus:ring-1 focus:ring-pvs-accent/50"
                disabled={loading}
              >
                {config.options.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          ))}
        </div>

        <button
          onClick={handleAnalyse}
          disabled={!query.trim() || loading}
          className="w-full py-2 bg-pvs-accent text-pvs-bg font-semibold rounded-lg hover:bg-pvs-accent-dim transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          {loading ? 'Analysing...' : 'Analyse Scenario'}
        </button>
      </div>

      {/* Results */}
      <div className="flex-1 overflow-y-auto p-4">
        {error && (
          <div className="p-3 bg-red-900/30 border border-red-500/30 rounded-lg text-sm text-red-400">
            {error}
          </div>
        )}

        {result && ds && (
          <div className="space-y-4">
            {/* Summary Dashboard */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="p-3 bg-pvs-card-light rounded-lg text-center">
                <div className="text-2xl font-bold text-white">{ds.summary.totalClauses}</div>
                <div className="text-xs text-pvs-text-dim">Clauses Found</div>
              </div>
              <div className="p-3 bg-red-900/30 rounded-lg text-center">
                <div className="text-2xl font-bold text-red-400">{ds.summary.criticalRisk}</div>
                <div className="text-xs text-red-400">Critical Risk</div>
              </div>
              <div className="p-3 bg-amber-900/20 rounded-lg text-center">
                <div className="text-2xl font-bold text-amber-400">{ds.summary.mandatory}</div>
                <div className="text-xs text-amber-400">Mandatory Clauses</div>
              </div>
              <div className="p-3 bg-pvs-accent/5 rounded-lg text-center">
                <div className="text-2xl font-bold text-pvs-accent">{ds.summary.documentsInvolved}</div>
                <div className="text-xs text-pvs-accent">Documents</div>
              </div>
            </div>

            {/* Warnings */}
            {ds.summary.requiresProfessionalAssessment && (
              <div className="p-3 bg-amber-900/20 border border-amber-500/30 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                <div className="text-xs text-amber-400">
                  <span className="font-semibold">Professional assessment required.</span> One or more clauses contain ALARP qualifiers,
                  site-specific requirements, or require assessment by a competent person.
                </div>
              </div>
            )}

            {result.contradictions?.length > 0 && (
              <div className="p-3 bg-red-900/30 border border-red-500/30 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                <div className="text-xs text-red-400">
                  <span className="font-semibold">{result.contradictions.length} potential conflict(s) detected</span> between clauses
                  from different Codes. Professional assessment of how these interact is required.
                </div>
              </div>
            )}

            {/* Ambiguity Highlights */}
            {ds.ambiguities.length > 0 && (
              <div className="p-3 bg-pvs-accent/5 border border-pvs-accent/20 rounded-lg">
                <div className="flex items-center gap-1 mb-2">
                  <Info className="w-4 h-4 text-pvs-accent" />
                  <span className="text-xs font-semibold text-pvs-accent">Regulatory Ambiguity Detected ({ds.ambiguities.length} clauses)</span>
                </div>
                <div className="space-y-1">
                  {ds.ambiguities.slice(0, 5).map((amb, i) => (
                    <div key={i} className="text-xs text-pvs-accent">
                      <span className="font-medium">{amb.document} {amb.section && `S.${amb.section}`}:</span>{' '}
                      {amb.types.map(t => t.description).join('; ')}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Multi-Code Intersections */}
            {result.intersections?.length > 0 && (
              <div className="p-3 bg-pvs-accent/5 border border-pvs-accent/20 rounded-lg flex items-start gap-2">
                <FileText className="w-4 h-4 text-pvs-accent mt-0.5 shrink-0" />
                <div className="text-xs text-pvs-accent">
                  {result.intersections[0].message}
                </div>
              </div>
            )}

            {/* Clauses by Risk Tier */}
            {['critical', 'elevated', 'standard'].map(tier => {
              const tierClauses = ds.byRiskTier[tier];
              if (!tierClauses || tierClauses.length === 0) return null;
              const colours = RISK_COLOURS[tier];

              return (
                <div key={tier}>
                  <h3 className="text-xs font-semibold text-pvs-text-muted uppercase tracking-wider mb-2">
                    {tier === 'critical' ? 'Critical Risk' : tier === 'elevated' ? 'Elevated Risk' : 'Standard'} ({tierClauses.length})
                  </h3>
                  <div className="space-y-2">
                    {tierClauses.map((clause) => {
                      const isExpanded = expandedClauses.has(clause.factId);
                      return (
                        <div key={clause.factId} className={`${colours.bg} border ${colours.border} rounded-lg overflow-hidden`}>
                          <button
                            onClick={() => toggleClause(clause.factId)}
                            className="w-full p-3 flex items-start gap-2 text-left"
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${colours.badge}`}>
                                  {tier.toUpperCase()}
                                </span>
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${OBLIGATION_COLOURS[clause.risk.obligationLevel] || OBLIGATION_COLOURS.INFORMATIONAL}`}>
                                  {clause.risk.obligationLevel}
                                </span>
                                {!clause.conditionMatch && (
                                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-900/30 text-orange-400">
                                    Potential non-alignment
                                  </span>
                                )}
                                <span className="text-[10px] text-pvs-text-dim">
                                  {(clause.relevanceScore * 100).toFixed(0)}% relevance
                                </span>
                              </div>
                              <p className="text-xs text-white font-medium">{clause.document}{clause.section ? `, Section ${clause.section}` : ''}</p>
                              <p className="text-xs text-pvs-text-muted mt-0.5 line-clamp-2">{clause.clause}</p>
                            </div>
                            {isExpanded ? <ChevronUp className="w-4 h-4 text-pvs-text-dim shrink-0" /> : <ChevronDown className="w-4 h-4 text-pvs-text-dim shrink-0" />}
                          </button>

                          {isExpanded && (
                            <div className="px-3 pb-3 space-y-2 border-t border-pvs-border pt-2">
                              <div>
                                <span className="text-[10px] font-semibold text-pvs-text-dim uppercase">Response</span>
                                <p className="text-xs text-white mt-0.5">{clause.verifiedAnswer}</p>
                              </div>
                              {clause.conditions?.length > 0 && (
                                <div>
                                  <span className="text-[10px] font-semibold text-pvs-text-dim uppercase">Conditions</span>
                                  <ul className="mt-0.5 space-y-0.5">
                                    {clause.conditions.map((c, i) => (
                                      <li key={i} className="text-xs text-pvs-text-muted">- {c}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {clause.conditionNotes?.length > 0 && (
                                <div className="p-2 bg-orange-900/30 border border-orange-500/30 rounded">
                                  <span className="text-[10px] font-semibold text-orange-400 uppercase">Non-alignment Notes</span>
                                  <ul className="mt-0.5 space-y-0.5">
                                    {clause.conditionNotes.map((n, i) => (
                                      <li key={i} className="text-xs text-orange-400">- {n}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {clause.sourceQuote?.exactQuote && (
                                <div>
                                  <span className="text-[10px] font-semibold text-pvs-text-dim uppercase">Source Text</span>
                                  <p className="text-xs text-pvs-text-dim italic mt-0.5 border-l-2 border-pvs-border pl-2">
                                    "{clause.sourceQuote.exactQuote}"
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* Audit Trail */}
            {result.audit && (
              <div className="mt-4 p-3 bg-pvs-card-light border border-pvs-border rounded-lg">
                <div className="text-[10px] text-pvs-text-dim space-y-0.5">
                  <div>System: VesselMind v{result.audit.systemVersion} | {result.audit.searchMethod} | {new Date(result.audit.timestamp).toLocaleString()}</div>
                  <div>{result.audit.disclaimer}</div>
                </div>
              </div>
            )}
          </div>
        )}

        {result && (!ds || result.totalClausesFound === 0) && (
          <div className="text-center py-8">
            <p className="text-sm text-pvs-text-dim">No applicable clauses identified for this scenario.</p>
            <p className="text-xs text-pvs-text-dim mt-1">Try adjusting your description or parameters, or consult the Codes of Practice directly.</p>
          </div>
        )}

        {!result && !loading && (
          <div className="text-center py-12">
            <Search className="w-10 h-10 text-pvs-text-dim mx-auto mb-3" />
            <p className="text-sm text-pvs-text-dim">Describe your scenario above to begin analysis.</p>
            <p className="text-xs text-pvs-text-dim mt-1">Optionally select parameters to filter by installation type, setting, etc.</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-pvs-border px-4 py-1.5 bg-amber-900/20">
        <p className="text-xs text-amber-400 text-center">
          Decision support tool — does not constitute a compliance determination. Professional validation required before application.
        </p>
      </div>
    </div>
  );
};

export default ScenarioAnalysis;
